import React from 'react';
import { ActionIcon, Menu } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import {
  IconDotsVertical,
  IconEdit,
  IconBolt,
  IconTrash,
} from '@tabler/icons-react';
import type { MRT_Row } from 'mantine-react-table';
import type { Rule } from '@billing/validation/rules';

interface RuleRowActionsProps {
  row: MRT_Row<Rule>;
  canModify: boolean;
  isMutating: boolean;
  onEdit: (row: MRT_Row<Rule>) => void;
  onActivate: (rule: Rule) => void;
  onActivateLatestVersion?: (rule: Rule) => void;
  onDeactivate: (rule: Rule) => void;
  onDeleteRuleSet: (rule: Rule) => void;
}

export const RuleRowActions: React.FC<RuleRowActionsProps> = ({
  row,
  canModify,
  isMutating,
  onEdit,
  onActivate,
  onActivateLatestVersion,
  onDeactivate,
  onDeleteRuleSet,
}) => {
  const { translate } = useTranslation();
  const rule = row.original;
  const canActivate =
    canModify && !isMutating && !rule.active && rule.status !== 'published';
  const canDeactivate =
    canModify && !isMutating && (rule.active || rule.status === 'published');

  return (
    <Menu shadow="md" width={220} withinPortal>
      <Menu.Target>
        <ActionIcon
          variant="transparent"
          aria-label={translate('pages:rules.rowActions.ariaLabel')}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <IconDotsVertical stroke={1.5} size={20} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconEdit size={14} />}
          disabled={!canModify || isMutating}
          onClick={() => onEdit(row)}
        >
          {translate('buttons.edit')}
        </Menu.Item>
        {onActivateLatestVersion && (
          <Menu.Item
            leftSection={<IconBolt size={14} />}
            disabled={!canModify || isMutating}
            onClick={() => onActivateLatestVersion(rule)}
          >
            {translate('pages:rules.rowActions.activateLatestVersion')}
          </Menu.Item>
        )}
        {rule.active || rule.status === 'published' ? (
          <Menu.Item
            leftSection={<IconBolt size={14} />}
            disabled={!canDeactivate}
            onClick={() => onDeactivate(rule)}
          >
            {translate('pages:rules.rowActions.deactivate')}
          </Menu.Item>
        ) : (
          <Menu.Item
            leftSection={<IconBolt size={14} />}
            disabled={!canActivate}
            onClick={() => onActivate(rule)}
          >
            {translate('pages:rules.rowActions.activateDraft')}
          </Menu.Item>
        )}
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconTrash size={14} />}
          color="red"
          disabled={!canModify || isMutating}
          onClick={() => onDeleteRuleSet(rule)}
        >
          {translate('buttons.delete')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
